import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { CronStateController } from '../../services/cron-state.js';
import * as cronApi from '../../services/cron-api.js';
import type { CronJob, CronExecution } from '../../types/cron.js';
import { getNextExecution, formatRelativeTime, isToday } from '../../utils/cron-utils.js';
import { navigateTo } from '../../services/router.js';

interface RoutineItem {
  job: CronJob;
  status: 'success' | 'failed' | 'running' | 'queued' | 'scheduled' | 'next-run' | 'disabled';
  time: string;
  summary: string;
  icon: string;
}

const STORAGE_KEY = 'compass:board-routines-collapsed';

@customElement('cp-board-routines')
export class CpBoardRoutines extends LitElement {
  createRenderRoot() { return this; }

  private _cronState = new CronStateController(this);

  @state() private _collapsed = false;
  @state() private _todayExecutions: CronExecution[] = [];

  connectedCallback(): void {
    super.connectedCallback();
    this._collapsed = localStorage.getItem(STORAGE_KEY) === 'true';
    this._cronState.loadJobs();
    this._loadTodayExecutions();
  }

  private async _loadTodayExecutions(): Promise<void> {
    // Load executions for all jobs to check today's runs
    const jobs = this._cronState.jobs;
    if (jobs.length === 0) {
      // Retry after jobs are loaded
      setTimeout(() => this._loadTodayExecutions(), 500);
      return;
    }

    const allExecs: CronExecution[] = [];
    for (const job of jobs) {
      try {
        const execs = await cronApi.getExecutions(job.id, 5);
        allExecs.push(...execs.filter(e => isToday(e.started_at)));
      } catch {
        // Ignore failed individual job execution fetches
      }
    }
    this._todayExecutions = allExecs;
  }

  private _toggleCollapse(): void {
    this._collapsed = !this._collapsed;
    localStorage.setItem(STORAGE_KEY, String(this._collapsed));
  }

  private _navigateToRoutine(job: CronJob): void {
    navigateTo('/cron');
  }

  private _getRoutineItems(): RoutineItem[] {
    const jobs = this._cronState.jobs;
    const qs = this._cronState.queueStatus;

    return jobs.map(job => {
      const enabled = job.enabled === 1;

      if (!enabled) {
        return { job, status: 'disabled' as const, time: '--:--', summary: 'Deaktiviert', icon: '\u23F8' };
      }

      // Check if currently running
      if (qs?.runningJobName === job.name || qs?.runningJobId === job.id) {
        return { job, status: 'running' as const, time: '', summary: 'Laeuft gerade...', icon: '\uD83D\uDD04' };
      }

      // Check today's executions for this job
      const jobExecs = this._todayExecutions.filter(e => e.cron_job_id === job.id);
      const latestExec = jobExecs.length > 0 ? jobExecs[0] : null;

      if (latestExec) {
        const execTime = new Date(latestExec.started_at);
        const timeStr = `${String(execTime.getHours()).padStart(2, '0')}:${String(execTime.getMinutes()).padStart(2, '0')}`;

        if (latestExec.status === 'success') {
          const summary = latestExec.output?.trim().slice(0, 60) || 'Erfolgreich';
          return { job, status: 'success' as const, time: timeStr, summary, icon: '\u2705' };
        }
        if (latestExec.status === 'failed' || latestExec.status === 'timeout') {
          const summary = latestExec.error?.trim().slice(0, 60) || 'Fehlgeschlagen';
          return { job, status: 'failed' as const, time: timeStr, summary, icon: '\u274C' };
        }
        if (latestExec.status === 'running') {
          return { job, status: 'running' as const, time: timeStr, summary: 'Laeuft gerade...', icon: '\uD83D\uDD04' };
        }
      }

      // Calculate next execution
      const nextExec = getNextExecution(job.schedule, job.timezone);
      if (nextExec) {
        if (isToday(nextExec)) {
          const timeStr = `${String(nextExec.getHours()).padStart(2, '0')}:${String(nextExec.getMinutes()).padStart(2, '0')}`;
          return { job, status: 'scheduled' as const, time: timeStr, summary: 'Geplant', icon: '\u23F3' };
        }
        const relTime = formatRelativeTime(nextExec);
        return { job, status: 'next-run' as const, time: relTime, summary: 'Naechster Lauf', icon: '\uD83D\uDCC5' };
      }

      return { job, status: 'scheduled' as const, time: '', summary: 'Geplant', icon: '\u23F3' };
    });
  }

  render() {
    const items = this._getRoutineItems();
    if (items.length === 0) return nothing;

    const completedCount = items.filter(i => i.status === 'success').length;
    const enabledCount = items.filter(i => i.status !== 'disabled').length;
    const nextScheduled = items.find(i => i.status === 'scheduled' || i.status === 'next-run');

    return html`
      <div class="cp-board-routines ${this._collapsed ? 'cp-board-routines--collapsed' : ''}">
        <button class="cp-board-routines__header" @click=${this._toggleCollapse}>
          <span class="cp-board-routines__header-left">
            <span class="cp-board-routines__toggle">${this._collapsed ? '\u25B6' : '\u25BC'}</span>
            <span class="cp-board-routines__title">Routinen heute</span>
          </span>
          <span class="cp-board-routines__count">${completedCount} von ${enabledCount}</span>
        </button>

        ${!this._collapsed ? html`
          <div class="cp-board-routines__list">
            ${items.map(item => html`
              <button
                class="cp-board-routines__item cp-board-routines__item--${item.status}"
                @click=${() => this._navigateToRoutine(item.job)}
              >
                <span class="cp-board-routines__item-icon">${item.icon}</span>
                <span class="cp-board-routines__item-name">${item.job.name}</span>
                <span class="cp-board-routines__item-time">${item.time}</span>
                <span class="cp-board-routines__item-summary">${item.summary}</span>
              </button>
            `)}
          </div>

          ${nextScheduled ? html`
            <div class="cp-board-routines__footer">
              <span class="cp-board-routines__next-run">
                \u25B6 Naechste Routine: ${nextScheduled.job.name} ${nextScheduled.time}
              </span>
            </div>
          ` : nothing}
        ` : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-board-routines': CpBoardRoutines;
  }
}
